import { a as _slicedToArray, e as _extends } from './_rollupPluginBabelHelpers-e71b1aad.js';
import * as React from 'react';
import React__default, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { a as __rest } from './tslib.es6-2748fcb6.js';
import { u as useIntl } from './useIntl-2a91ee7b.js';
import { I as IntlProvider } from './provider-936ecd10.js';

var DisplayName;
(function (DisplayName) {
    DisplayName["formatDate"] = "FormattedDate";
    DisplayName["formatTime"] = "FormattedTime";
    DisplayName["formatNumber"] = "FormattedNumber";
    DisplayName["formatList"] = "FormattedList";
    // Note that this DisplayName is the locale display name, not to be confused with
    // the name of the enum, which is for React component display name in dev tools.
    DisplayName["formatDisplayName"] = "FormattedDisplayName";
})(DisplayName || (DisplayName = {}));
var DisplayNameParts;
(function (DisplayNameParts) {
    DisplayNameParts["formatDate"] = "FormattedDateParts";
    DisplayNameParts["formatTime"] = "FormattedTimeParts";
    DisplayNameParts["formatNumber"] = "FormattedNumberParts";
    DisplayNameParts["formatList"] = "FormattedListParts";
})(DisplayNameParts || (DisplayNameParts = {}));
function createFormattedDateTimePartsComponent(name) {
    var ComponentParts = function (props) {
        var intl = useIntl();
        var value = props.value, children = props.children, formatProps = __rest(props, ["value", "children"]);
        var date = typeof value === 'string' ? new Date(value || 0) : value;
        var formattedParts = name === 'formatDate'
            ? intl.formatDateToParts(date, formatProps)
            : intl.formatTimeToParts(date, formatProps);
        return children(formattedParts);
    };
    ComponentParts.displayName = DisplayNameParts[name];
    return ComponentParts;
}
function createFormattedComponent(name) {
    var Component = function (props) {
        var intl = useIntl();
        var value = props.value, children = props.children, formatProps = __rest(props, ["value", "children"]);
        // TODO: fix TS type definition for localeMatcher upstream
        var formattedValue = intl[name](value, formatProps);
        if (typeof children === 'function') {
            return children(formattedValue);
        }
        var Text = intl.textComponent || React.Fragment;
        return React.createElement(Text, null, formattedValue);
    };
    Component.displayName = DisplayName[name];
    return Component;
}

// IMPORTANT: Explicit here to prevent api-extractor from outputing `import('./src/types').CustomFormatConfig`
var FormattedDate = createFormattedComponent('formatDate');
createFormattedComponent('formatTime');
var FormattedNumber$1 = createFormattedComponent('formatNumber');
createFormattedComponent('formatList');
createFormattedComponent('formatDisplayName');
createFormattedDateTimePartsComponent('formatDate');
createFormattedDateTimePartsComponent('formatTime');

function getCurrency(formattedNumber, className, currencyDisplay, currency) {
  var numberMatched = formattedNumber.match(/[\d]+((\.|,|’)\d+)*/g);
  var isNumber = numberMatched !== null;
  var number = isNumber ? numberMatched[0] : false;
  var isCurrencyCodeFirst = Number.isNaN(parseInt(formattedNumber.charAt(0), 10));
  var currencyValue = currencyDisplay === 'symbol' ? currency.symbol : currency.code;
  return /*#__PURE__*/React__default.createElement("span", {
    className: "bf-formatted-number-wrapper ".concat(className)
  }, number ? isCurrencyCodeFirst ? "".concat(currencyValue, " ").concat(number) : "".concat(number, " ").concat(currencyValue) : null, !isNumber && /*#__PURE__*/React__default.createElement("span", null, formattedNumber));
}
function getStyledCurrency(formattedNumber, className, currencyDisplay, currency) {
  var number = formattedNumber.match(/[\d\s]+((\.|,|’|')\d+)*/g)[0];
  var decimal = number.slice(-2);
  var integer = number.substr(0, number.length - 2);
  // currency code first
  if (Number.isNaN(parseInt(formattedNumber.charAt(0), 10))) {
    return /*#__PURE__*/React__default.createElement("span", {
      className: "bf-formatted-number-wrapper is-styled ".concat(className)
    }, /*#__PURE__*/React__default.createElement("span", {
      className: "currency-symbol"
    }, currencyDisplay === 'symbol' ? currency.symbol : currency.code), /*#__PURE__*/React__default.createElement("span", {
      className: "number-wrapper"
    }, /*#__PURE__*/React__default.createElement("span", {
      className: "integer"
    }, integer), /*#__PURE__*/React__default.createElement("span", {
      className: "decimal"
    }, decimal)));
  }
  return /*#__PURE__*/React__default.createElement("span", {
    className: "bf-formatted-number-wrapper is-styled ".concat(className)
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "number-wrapper"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "integer"
  }, integer), /*#__PURE__*/React__default.createElement("span", {
    className: "decimal"
  }, decimal)), /*#__PURE__*/React__default.createElement("span", {
    className: "currency-symbol"
  }, currencyDisplay === 'symbol' ? currency.symbol : currency.code));
}
function getPercent(formattedNumber, className) {
  return /*#__PURE__*/React__default.createElement("span", {
    className: "bf-formatted-number-wrapper ".concat(className)
  }, "".concat(formattedNumber, "%"));
}

/**
 * An internal state was implemented to force a re-rendering of the component on the client side
 * due to a bug in the Intl FormattedNumber component which returns a format on the server and a
 * different one on the client side.
 */

var FormattedNumber = function FormattedNumber(props) {
  var currency = props.currency,
    percent = props.percent,
    styled = props.styled,
    className = props.className,
    currencyDisplay = props.currencyDisplay,
    language = props.language;
  var _useState = useState('server'),
    _useState2 = _slicedToArray(_useState, 2),
    key = _useState2[0],
    setKey = _useState2[1];
  useEffect(function () {
    setKey('client');
  }, []);
  var getConfig = function getConfig() {
    var value = props.value,
      fractionDigits = props.fractionDigits;
    var config = {
      value: value
    };
    if (currency) {
      config.maximumFractionDigits = fractionDigits;
      config.minimumFractionDigits = fractionDigits;
      config.style = 'currency';
      config.currency = currency.code;
      config.currencyDisplay = currencyDisplay;
    } else if (percent) {
      config.maximumFractionDigits = 0;
      config.value = Math.floor(config.value);
    }
    return config;
  };
  var config = getConfig();
  return /*#__PURE__*/React__default.createElement(IntlProvider, {
    locale: language
  }, /*#__PURE__*/React__default.createElement(FormattedNumber$1, _extends({
    key: key
  }, config), function (formattedNumber) {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, currency && !styled ? getCurrency(formattedNumber, className, currencyDisplay, currency) : null, currency && styled ? getStyledCurrency(formattedNumber, className, currencyDisplay, currency) : null, percent ? getPercent(formattedNumber, className) : null);
  }));
};
FormattedNumber.defaultProps = {
  currency: null,
  currencyDisplay: null,
  percent: null,
  styled: null,
  className: '',
  fractionDigits: 2,
  language: 'en'
};
FormattedNumber.propTypes = {
  /**
  Main value
   */
  value: PropTypes.number.isRequired,
  /**
  Currency object (code, symbol)
   */
  currency: PropTypes.shape({
    code: PropTypes.string.isRequired,
    symbol: PropTypes.string.isRequired
  }),
  /**
  Show currency as symbol or code
   */
  currencyDisplay: PropTypes.string,
  /**
  Add % symbol after value
   */
  percent: PropTypes.bool,
  /**
  Show the price styled (currency, integer, decimal)
   */
  styled: PropTypes.bool,
  /**
  Custom className
   */
  className: PropTypes.string,
  /**
  Number of decimals
   */
  fractionDigits: PropTypes.number,
  /**
  Intl language
   */
  language: PropTypes.string
};

export { FormattedDate as F, FormattedNumber as a };
//# sourceMappingURL=index-f48c5efa.js.map
